import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import MD from './MD';
import SlashMenu, { SLASH_COMMANDS } from './SlashMenu';
import { Keys, get as lsGet, set as lsSet } from '../lib/storage';

const FALLBACK_MODELS = { 'qwen': { name: 'Lade…' } };

const TMPL_LABELS = { default:'Standard', code:'Code', erklaer:'Erklären', kreativ:'Kreativ', nuechtern:'Nüchtern' };

const SUGGESTED_PROMPTS = [
  { label: 'Server-Status', text: '/status', hint: 'Tool — Live Service-Check' },
  { label: 'Web-Suche', text: '/web ', hint: 'Tool — DuckDuckGo' },
  { label: 'Logs lesen', text: '/logs chat', hint: 'Tool — letzte 20 Zeilen' },
  { label: 'Code erklären', text: 'Erkläre mir was dieser Code macht:\n\n```python\n\n```', hint: 'Standard-Chat mit Modus „Code"' },
];

/** Parse "/cmd ARGS..." → matched command + arg-string. Null wenn kein Slash-Command. */
function parseSlashInput(text) {
  if (!text.startsWith('/')) return null;
  const m = text.match(/^\/(\w+)(?:\s+(.*))?$/s);
  if (!m) return null;
  const cmd = SLASH_COMMANDS.find(c => c.trigger === m[1].toLowerCase());
  if (!cmd) return null;
  return { cmd, args: (m[2] || '').trim() };
}

export default function Chat({ authToken, onLogout, currentChatId, onChatChanged, initialPrompt, onInitialPromptConsumed, onScheduleFromChat, onHelp }) {
  const [messages,     setMessages]     = useState([]);
  const [input,        setInput]        = useState('');
  const [model,        setModel]        = useState(() => lsGet(Keys.MODEL, 'qwen'));
  const [models,       setModels]       = useState(FALLBACK_MODELS);
  const [temperature,  setTemperature]  = useState(() => parseFloat(lsGet(Keys.TEMP, '0.7')) || 0.7);
  const [template,     setTemplate]     = useState(() => lsGet(Keys.TMPL, 'default'));
  const [loading,      setLoading]      = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [voiceActive,  setVoiceActive]  = useState(false);
  const [copiedIdx,    setCopiedIdx]    = useState(null);
  const [slashOpen,    setSlashOpen]    = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [dragOver,     setDragOver]     = useState(false);
  const [now,          setNow]          = useState(Date.now()); // tickt während Stream

  const fileInputRef = useRef(null);
  const endRef      = useRef(null);
  const msgsRef     = useRef(messages);
  const abortRef    = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { msgsRef.current = messages; }, [messages]);

  // Cleanup: laufenden Stream abbrechen bei Unmount/Tab-Wechsel
  useEffect(() => () => abortRef.current?.abort(), []);

  // Chat aus DB laden wenn currentChatId wechselt
  useEffect(() => {
    if (currentChatId == null) { setMessages([]); return; }
    let cancelled = false;
    fetch(`/api/chats/${currentChatId}`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(r => {
        if (r.status === 401) { onLogout?.(); return null; }
        if (!r.ok) return null;
        return r.json();
      })
      .then(d => {
        if (cancelled || !d) return;
        const msgs = (d.messages || []).map(m => ({ role: m.role, content: m.content }));
        setMessages(msgs);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentChatId, authToken, onLogout]);

  useEffect(() => { lsSet(Keys.MODEL, model);              }, [model]);
  useEffect(() => { lsSet(Keys.TEMP,  String(temperature)); }, [temperature]);
  useEffect(() => { lsSet(Keys.TMPL,  template);           }, [template]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Modell-Liste vom Server (public-Endpoint)
  useEffect(() => {
    fetch('/api/models')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.models && Object.keys(d.models).length > 0) {
          setModels(d.models);
          setModel(prev => d.models[prev] ? prev : Object.keys(d.models)[0]);
        }
      })
      .catch(() => {});
  }, []);

  // Auto-grow textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }, [input]);

  // Initial-Prompt von außen (z.B. Report→Chat-Brücke) einspielen
  useEffect(() => {
    if (initialPrompt) {
      setInput(initialPrompt);
      onInitialPromptConsumed?.();
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [initialPrompt, onInitialPromptConsumed]);

  // Slash-Menü öffnen wenn Input mit "/" startet und noch kein Argument folgt
  useEffect(() => {
    setSlashOpen(input.startsWith('/') && !input.includes(' ') && input.length <= 16);
  }, [input]);

  // Während Stream tickt now alle 2s — damit Loading-Hint nach 8s erscheint
  // (sonst rendert React nicht neu solange keine Tokens reinkommen)
  useEffect(() => {
    const isStreaming = messages.some(m => m.streaming);
    if (!isStreaming) return;
    const id = setInterval(() => setNow(Date.now()), 2000);
    return () => clearInterval(id);
  }, [messages]);

  const slashFilter = input.startsWith('/') ? input.slice(1).split(/\s/)[0] : '';

  const onSlashSelect = (cmd) => {
    // Bei Tools mit Parameter: Trigger + Space, damit User direkt Argument tippt
    setInput(cmd.param ? `/${cmd.trigger} ` : `/${cmd.trigger}`);
    setSlashOpen(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const sendStream = useCallback(async (text, history) => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch('/api/chat/stream', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body:    JSON.stringify({ message: text, model, history, temperature, template, chat_id: currentChatId ?? null }),
        signal:  ctrl.signal,
      });
      if (res.status === 401) {
        setMessages(prev => { const l = prev[prev.length-1]; return l?.streaming ? prev.slice(0,-1) : prev; });
        onLogout(); return;
      }
      if (!res.ok) {
        const e = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(e.error || res.statusText);
      }
      const reader = res.body.getReader();
      const dec    = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6);
          if (raw === '[DONE]') break;
          let ev; try { ev = JSON.parse(raw); } catch { continue; }
          if (ev.type === 'token') {
            setMessages(prev => {
              const u=[...prev]; const l=u[u.length-1];
              const elapsed = Math.max(0.1, (Date.now() - (l.startedAt || Date.now())) / 1000);
              const newContent = l.content + ev.text;
              const tokens = Math.ceil(newContent.length / 4);
              u[u.length-1] = {
                ...l,
                content: newContent,
                startedAt: l.startedAt || Date.now(),
                tokensSoFar: tokens,
                tps: Math.round(tokens / elapsed),
              };
              return u;
            });
          } else if (ev.type === 'reasoning') {
            // R1-Style Reasoning-Modelle: kennzeichne den Denk-Prozess separat
            setMessages(prev => {
              const u = [...prev]; const l = u[u.length-1];
              u[u.length-1] = {
                ...l,
                reasoning: (l.reasoning || '') + ev.text,
                hasReasoning: true,
              };
              return u;
            });
          } else if (ev.type === 'tool_start') {
            setMessages(prev => { const u=[...prev],l=u[u.length-1]; u[u.length-1]={...l,content:l.content+'\n\n_Führe Tools aus…_'}; return u; });
          } else if (ev.type === 'done') {
            setMessages(prev => {
              const u=[...prev];
              u[u.length-1]={ role:'assistant', streaming:false,
                content: ev.cleaned_content || u[u.length-1].content,
                model: ev.model, toolsCalled: ev.tools_called||0, smartRouted: ev.smart_routed };
              return u;
            });
            if (ev.chat_id != null && ev.chat_id !== currentChatId) {
              onChatChanged?.(ev.chat_id, ev.new_chat);
            } else if (ev.chat_id != null) {
              onChatChanged?.(ev.chat_id, false);
            }
          } else if (ev.type === 'error') throw new Error(ev.message);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setMessages(prev => { const u=[...prev],l=u[u.length-1]; if(l?.streaming)u[u.length-1]={...l,streaming:false}; return u; });
        return;
      }
      setMessages(prev => { const u=[...prev]; u[u.length-1]={role:'error',content:`Fehler: ${err.message}`}; return u; });
    } finally {
      abortRef.current = null;
      setMessages(prev => { const l=prev[prev.length-1]; if(!l?.streaming)return prev; const u=[...prev]; u[u.length-1]={...l,streaming:false}; return u; });
    }
  }, [model, authToken, onLogout, temperature, template, currentChatId, onChatChanged]);

  /** Direkter Tool-Aufruf via /api/tools/exec — ohne LLM-Loop, schnelles Ergebnis. */
  const execTool = useCallback(async (toolName, params, userBubbleText) => {
    setMessages(prev => [
      ...prev,
      { role: 'user',      content: userBubbleText },
      { role: 'assistant', content: '', streaming: true, model: `tool:${toolName}` },
    ]);
    setLoading(true);
    try {
      const res = await fetch('/api/tools/exec', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body:    JSON.stringify({ tool_name: toolName, params }),
      });
      if (res.status === 401) { onLogout(); return; }
      const d = await res.json().catch(() => ({}));
      const result = d.result || (d.error ? `**Fehler:** ${d.error}` : '*(Keine Antwort)*');
      const md = `**[${toolName}]** _(in ${d.took_ms ?? '?'}ms)_\n\n${result}`;
      setMessages(prev => {
        const u = [...prev];
        u[u.length-1] = { role: 'assistant', streaming: false, content: md, model: `tool:${toolName}`, toolsCalled: 1 };
        return u;
      });
    } catch (e) {
      setMessages(prev => {
        const u = [...prev];
        u[u.length-1] = { role: 'error', content: `Tool-Fehler: ${e.message}` };
        return u;
      });
    } finally {
      setLoading(false);
      // Phase 9b: konsistent zum Stream-Pfad — Fokus zurück ins Eingabefeld.
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [authToken, onLogout]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();

    // Slash-Command-Pfad: direkter Tool-Aufruf statt LLM
    const slash = parseSlashInput(text);
    if (slash) {
      const { cmd, args } = slash;
      // Spezial-Aktion /schedule: Jobs-Tab mit vorbefülltem Topic öffnen
      if (cmd.special === 'job') {
        if (!args) {
          setMessages(prev => [
            ...prev,
            { role: 'user', content: text },
            { role: 'error', content: `Argument fehlt: \`topic\`. Beispiel: \`/schedule KI-Regulierung Wochen-Update\`` },
          ]);
          setInput('');
          return;
        }
        setInput('');
        onScheduleFromChat?.(args);
        return;
      }
      // Spezial-Aktion /ops: 4-in-1 (status/service/logs/system) → passende Backend-Tools
      if (cmd.special === 'ops') {
        const parts = (args || '').trim().split(/\s+/).filter(Boolean);
        const sub = (parts[0] || '').toLowerCase();
        const rest = parts.slice(1).join(' ');
        setInput('');
        if (!sub || sub === 'status') {
          await execTool('server_status', {}, text);
        } else if (sub === 'system') {
          await execTool('system_info', {}, text);
        } else if (sub === 'service') {
          if (!rest) {
            setMessages(prev => [
              ...prev,
              { role: 'user', content: text },
              { role: 'error', content: 'Argument fehlt: `service`. Beispiel: `/ops service ollama`' },
            ]);
            return;
          }
          await execTool('check_service', { service: rest }, text);
        } else if (sub === 'logs') {
          if (!rest) {
            setMessages(prev => [
              ...prev,
              { role: 'user', content: text },
              { role: 'error', content: 'Argument fehlt: `logfile`. Beispiel: `/ops logs chat`' },
            ]);
            return;
          }
          await execTool('last_logs', { logfile: rest }, text);
        } else {
          setMessages(prev => [
            ...prev,
            { role: 'user', content: text },
            { role: 'error', content: `Unbekanntes /ops-Subkommando: \`${sub}\`. Erlaubt: leer | service <name> | logs <name> | system` },
          ]);
        }
        return;
      }
      if (cmd.tool) {
        const params = cmd.param ? { [cmd.param]: args } : {};
        if (cmd.param && !args) {
          setMessages(prev => [
            ...prev,
            { role: 'user', content: text },
            { role: 'error', content: `Argument fehlt: \`${cmd.param}\`. Beispiel: \`/${cmd.trigger} ${cmd.placeholder}\`` },
          ]);
          setInput('');
          return;
        }
        setInput('');
        await execTool(cmd.tool, params, text);
        return;
      }
    }

    // Standard-Pfad: LLM-Stream
    const history = msgsRef.current
      .filter(m => m.role==='user'||m.role==='assistant').slice(-20)
      .map(m => ({ role:m.role, content:m.content }));
    setMessages(prev => [...prev, {role:'user',content:text}, {role:'assistant',content:'',streaming:true,startedAt:Date.now()}]);
    setInput('');
    setLoading(true);
    await sendStream(text, history);
    setLoading(false);
    // Phase 9b: Fokus zurück ins Eingabefeld nach Stream-Ende — Power-User
    // schreibt sofort weiter, Screen-Reader bekommt klares Signal "fertig".
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [input, loading, sendStream, execTool]);

  const handleRegenerate = useCallback(async (assistantIdx) => {
    if (loading) return;
    const msgs = msgsRef.current;
    let ui = assistantIdx - 1;
    while (ui >= 0 && msgs[ui].role !== 'user') ui--;
    if (ui < 0) return;
    const text    = msgs[ui].content;
    const history = msgs.slice(0,ui).filter(m=>m.role==='user'||m.role==='assistant').map(m=>({role:m.role,content:m.content}));
    setMessages(prev => [...prev.slice(0,assistantIdx), {role:'assistant',content:'',streaming:true,startedAt:Date.now()}]);
    setLoading(true);
    await sendStream(text, history);
    setLoading(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [loading, sendStream]);

  const handleStop   = () => abortRef.current?.abort();
  const handleClear  = () => setMessages([]);
  const handleCopy   = (content, idx) => {
    navigator.clipboard.writeText(content).then(() => { setCopiedIdx(idx); setTimeout(()=>setCopiedIdx(null),2000); });
  };
  const handleExport = useCallback(() => {
    const body = messages.filter(m=>m.role==='user'||m.role==='assistant')
      .map(m=>m.role==='user'?`**Du:** ${m.content}`:`**${m.model||'AI'}:**\n\n${m.content}`)
      .join('\n\n---\n\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`# Chat Export\n\n${body}`],{type:'text/markdown'}));
    a.download = `chat-${new Date().toISOString().slice(0,10)}.md`;
    a.click();
  }, [messages]);

  const handleUpload = useCallback(async (file) => {
    if (!file || uploading) return;
    if (file.size > 5 * 1024 * 1024) {
      setMessages(prev => [...prev, { role: 'error', content: 'Datei zu groß (max 5 MB)' }]);
      return;
    }
    // Falls noch kein Chat existiert: Server legt einen an beim ersten add_message —
    // aber Upload braucht einen chat_id. Wir erzeugen vorher.
    let cid = currentChatId;
    if (cid == null) {
      const r = await fetch('/api/chats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        setMessages(prev => [...prev, { role: 'error', content: 'Konnte Chat nicht erstellen.' }]);
        return;
      }
      const d = await r.json();
      cid = d.id;
      onChatChanged?.(cid, true);
    }
    setUploading(true);
    setMessages(prev => [...prev, { role: 'user', content: `📎 Lade hoch: **${file.name}**…`, _temp: true }]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`/api/chats/${cid}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      // Optimistic-Bubble durch echte Server-Message ersetzen — pragmatisch via reload
      setMessages(prev => prev.filter(m => !m._temp));
      // Reload from server (zeigt 📎 + extrahierten Text)
      const fetched = await fetch(`/api/chats/${cid}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const cd = await fetched.json();
      const msgs = (cd.messages || []).map(m => ({ role: m.role, content: m.content }));
      setMessages(msgs);
    } catch (e) {
      setMessages(prev => [
        ...prev.filter(m => !m._temp),
        { role: 'error', content: `Upload-Fehler: ${e.message}` },
      ]);
    } finally {
      setUploading(false);
    }
  }, [authToken, currentChatId, onChatChanged, uploading]);

  const handleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = navigator.language || 'de-DE';
    rec.interimResults = false;
    rec.onresult = e => setInput(p => p + e.results[0][0].transcript);
    rec.onend    = () => setVoiceActive(false);
    rec.onerror  = () => setVoiceActive(false);
    setVoiceActive(true);
    rec.start();
  };

  const lastAssistantIdx = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && !messages[i].streaming) return i;
    }
    return -1;
  }, [messages]);

  const hasSpeech = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  return (
    <div
      className={`chat-view ${dragOver ? 'drag-over' : ''}`}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleUpload(f);
      }}
    >

      <div className="chat-header">
        <select value={model} onChange={e => setModel(e.target.value)} className="model-picker">
          {Object.entries(models).map(([k,c]) => <option key={k} value={k}>{c.name}</option>)}
        </select>
        <div className="chat-header-actions">
          {onHelp && <button onClick={() => onHelp('chat')} className="hdr-btn" title="Hilfe zum Chat öffnen">❓</button>}
          <button onClick={() => setShowSettings(s=>!s)} className={`hdr-btn ${showSettings?'hdr-btn-active':''}`} title="Einstellungen">⚙</button>
          <button onClick={handleExport} className="hdr-btn" title="Als Markdown exportieren" disabled={!messages.length}>↓</button>
          <button onClick={handleClear} className="hdr-btn" title="Verlauf löschen" disabled={!messages.length}>⌫</button>
        </div>
      </div>

      {showSettings && (
        <div className="settings-drawer">
          <div className="setting-row">
            <span className="setting-label">Modus</span>
            <div className="pill-group">
              {Object.entries(TMPL_LABELS).map(([k,v]) => (
                <button key={k} className={`pill ${template===k?'pill-active':''}`} onClick={() => setTemplate(k)} title={`Antwort-Stil: ${v}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="setting-row">
            <span className="setting-label">Kreativität</span>
            <div className="slider-row">
              <span className="slider-cap">präzise</span>
              <input type="range" min="0" max="1.5" step="0.1" value={temperature}
                onChange={e => setTemperature(parseFloat(e.target.value))} className="slider" />
              <span className="slider-cap">kreativ</span>
              <span className="slider-val">{temperature.toFixed(1)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="messages-area" role="log" aria-live="polite" aria-relevant="additions text" aria-label="Chat-Verlauf">
        {messages.length === 0 && (
          <div className="chat-empty">
            <div className="chat-empty-icon">◈</div>
            <p className="chat-empty-title">Bereit für deine Frage</p>
            <p className="chat-empty-sub">Lokale Modelle auf dem Mac Mini — wähle oben ein Modell oder nutze Auto-Routing</p>
            <div className="empty-suggestions">
              <p className="empty-suggestions-label">Vorschläge zum Starten:</p>
              <div className="empty-suggestions-grid">
                {SUGGESTED_PROMPTS.map((s, i) => (
                  <button
                    key={i}
                    className="empty-suggestion-card"
                    onClick={() => { setInput(s.text); textareaRef.current?.focus(); }}
                    title="Diesen Vorschlag ins Eingabefeld übernehmen"
                  >
                    <span className="empty-suggestion-label">{s.label}</span>
                    <span className="empty-suggestion-hint">{s.hint}</span>
                  </button>
                ))}
              </div>
              <p className="empty-tip">Tipp: tippe <kbd>/</kbd> für Slash-Befehle (Tools direkt aufrufen)</p>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`msg-row msg-${msg.role}`}>
            {msg.role === 'user' ? (
              <div className="msg-bubble msg-user"><span>{msg.content}</span></div>
            ) : msg.role === 'error' ? (
              <div className="msg-bubble msg-error"><span>{msg.content}</span></div>
            ) : (
              <div className="msg-bubble msg-assistant">
                {msg.hasReasoning && msg.reasoning && (
                  <details className="msg-reasoning">
                    <summary>🧠 Denkt nach… ({msg.reasoning.length} Zeichen)</summary>
                    <MD content={msg.reasoning} />
                  </details>
                )}
                <MD content={msg.content || (msg.streaming ? '' : msg.reasoning || '')} />
                {msg.streaming && <span className="blink-cursor" />}
                {msg.streaming && (msg.tokensSoFar || 0) > 5 && (
                  <div className="msg-stream-meta">
                    <span className="stream-pulse" />
                    {msg.tokensSoFar} Token · {msg.tps || 0} t/s
                  </div>
                )}
                {msg.streaming && (msg.tokensSoFar || 0) <= 1 && msg.startedAt &&
                  (now - msg.startedAt) > 8000 && (
                  <div className="msg-stream-meta msg-loading-hint">
                    <span className="stream-pulse" />
                    Modell wird geladen — kann bei großen Modellen (Qwen/Gemma) bis zu 60 s dauern…
                  </div>
                )}
                {msg.model && !msg.streaming && (
                  <div className="msg-meta">
                    <span className="msg-model-badge">{msg.model}{msg.smartRouted?' · auto':''}</span>
                    {msg.toolsCalled > 0 && (
                      <span className="msg-tools-badge">{msg.toolsCalled} Tool{msg.toolsCalled>1?'s':''}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {(msg.role==='user'||msg.role==='assistant') && !msg.streaming && (
              <div className="msg-actions">
                <button
                  className={`msg-action-btn ${copiedIdx===idx?'msg-action-copied':''}`}
                  onClick={() => handleCopy(msg.content, idx)}
                  title="Kopieren"
                >
                  {copiedIdx===idx ? '✓' : '⎘'}
                </button>
                {idx === lastAssistantIdx && msg.role === 'assistant' && (
                  <button className="msg-action-btn" onClick={() => handleRegenerate(idx)} disabled={loading} title="Neu generieren">
                    ↺
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="input-dock">
        {slashOpen && (
          <SlashMenu
            filter={slashFilter}
            onSelect={onSlashSelect}
            onClose={() => setSlashOpen(false)}
          />
        )}
        <div className="input-box">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key==='Enter'&&!e.shiftKey&&!slashOpen) { e.preventDefault(); handleSend(); } }}
            placeholder="Nachricht… (Enter senden, Shift+Enter Umbruch, / für Befehle)"
            disabled={loading}
            rows={1}
            className="chat-textarea"
            aria-label="Chat-Nachricht eingeben"
          />
          <div className="input-box-footer">
            <div className="input-left">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || uploading}
                className="input-icon-btn"
                title="Datei anhängen (PDF, MD, TXT)"
              >📎</button>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.md,.txt,.csv,.json,.log,.markdown"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                  e.target.value = '';
                }}
              />
              {hasSpeech && (
                <button onClick={handleVoice} disabled={loading || voiceActive} className={`input-icon-btn ${voiceActive?'recording':''}`} title="Spracheingabe">♪</button>
              )}
              <span className="input-model-hint">{models[model]?.name || model}</span>
              <span
                className={`input-token-counter ${Math.ceil(input.length/4) > 1800 ? 'warn' : ''}`}
                title="Heuristische Schätzung (~4 Zeichen = 1 Token). Limit 2000 Tokens pro Nachricht."
              >
                ~{Math.ceil(input.length / 4)} Token
              </span>
            </div>
            <div className="input-right">
              {loading
                ? <button onClick={handleStop} className="btn-stop" title="Abbrechen">◼ Stop</button>
                : <button onClick={handleSend} disabled={!input.trim()} className="btn-send" title="Nachricht senden (Enter)">Senden ↑</button>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
